import { useEffect, useMemo, useRef } from 'react';
import { MessageSquareOff, TriangleAlert } from 'lucide-react';

import AssistantMessage from './AssistantMessage';
import Composer from './Composer';
import ContextChip from './ContextChip';
import EmptyState from './EmptyState';
import UserMessage from './UserMessage';

import { AgentRefsContext, useAgentPanelContext } from '@/app/agentConversation/_context';
import { withoutTurnEcho } from '@/app/agentConversation/_helpers';

import { Button } from '@/app/ui/button';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/app/ui/empty';
import { Error } from '@/app/ui/error';
import { Loading } from '@/app/ui/loading';
import { useAgentConversation } from '@/data/agentConversation';

/** The current conversation, or a new one when none is selected. */
const Chat = () => {
  const panel = useAgentPanelContext();
  if (!panel.conversationUid) return <ChatView conversation={null} />;
  return <SavedChat uid={panel.conversationUid} />;
};

const SavedChat = ({ uid }) => {
  const conversation = useAgentConversation(uid);
  if (conversation.isLoading && !conversation.data) return <Loading />;
  if (conversation.error?.status === 404) return <MissingConversation />;
  if (conversation.error) {
    return (
      <div className="p-4">
        <Error message={conversation.error.message} />
      </div>
    );
  }
  return <ChatView conversation={conversation.data} />;
};

// Deleted elsewhere, or stored for a company the user has since switched away from.
const MissingConversation = () => {
  const panel = useAgentPanelContext();

  return (
    <Empty className="border-0" data-testid="agent-conversation-missing">
      <EmptyHeader>
        <EmptyMedia>
          <MessageSquareOff />
        </EmptyMedia>
        <EmptyTitle>Conversation Not Found</EmptyTitle>
        <EmptyDescription>It may have been deleted, or it belongs to another company.</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button size="sm" onClick={panel.startNewConversation}>
          Start a New Conversation
        </Button>
      </EmptyContent>
    </Empty>
  );
};

// Within this distance of the bottom, new content keeps the view pinned there;
// further up, the user is reading back and is left alone.
const STICK_TO_BOTTOM_PX = 120;

const ChatView = ({ conversation }) => {
  const panel = useAgentPanelContext();
  const { turn } = panel;
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  // Whether the user was at the bottom when they last scrolled. Measured on
  // scroll, not after new content lands, which would already be taller.
  const isPinnedRef = useRef(true);
  const messages = withoutTurnEcho(conversation?.messages ?? [], turn);
  // The live turn's summaries over the saved ones: they are the newer read.
  const refs = useMemo(() => ({ ...conversation?.refs, ...turn?.refs }), [conversation?.refs, turn?.refs]);
  const isEmpty = !messages.length && !turn;
  const lastIndex = messages.length - 1;
  // A new message the user just sent: whatever they were reading, they want
  // to see it go, so the view pins to the bottom again.
  const sentText = turn?.text;

  const lastScrollTopRef = useRef(0);

  // Pinned near the bottom; unpinned only by a scroll that went up and left
  // the bottom — the user reading back. A scroll the layout causes (the saved
  // reply replacing the live one, content clamping) does not unpin.
  const handleScroll = (event) => {
    const element = event.currentTarget;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < STICK_TO_BOTTOM_PX;
    const movedUp = element.scrollTop < lastScrollTopRef.current - 2;
    if (isNearBottom) isPinnedRef.current = true;
    else if (movedUp) isPinnedRef.current = false;
    lastScrollTopRef.current = element.scrollTop;
  };

  useEffect(() => {
    if (!sentText || !scrollRef.current) return;
    isPinnedRef.current = true;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [sentText]);

  // Follows the content's real height rather than a count of what rendered: a
  // chart, table or a wide code block settles its height a moment after it mounts, and
  // scrolling before then leaves the end of the reply below the fold.
  useEffect(() => {
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!scroller || !content) return undefined;
    const observer = new ResizeObserver(() => {
      if (isPinnedRef.current) scroller.scrollTop = scroller.scrollHeight;
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <AgentRefsContext.Provider value={refs}>
      {/* No scroll anchoring: the browser would move the view itself when the
          reply swaps in, which reads as the user scrolling away. */}
      <div ref={scrollRef} onScroll={handleScroll} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 [overflow-anchor:none]" data-testid="agent-messages">
        <div ref={contentRef}>
          {isEmpty ? (
            <EmptyState view={panel.context.view} tab={panel.context.tab} onSend={panel.send} />
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) =>
                message.role === 'user' ? (
                  <UserMessage key={message.uid} text={message.blocks[0]?.text ?? ''} />
                ) : (
                  <AssistantMessage key={message.uid} blocks={message.blocks} status={message.status} isAskPending={!turn && index === lastIndex} onAnswer={panel.send} />
                )
              )}
              {conversation?.is_interrupted && !turn && (
                <div className="text-muted-foreground flex items-center gap-1.5 text-xs" data-testid="agent-interrupted">
                  <TriangleAlert className="size-3.5" />
                  This reply was interrupted. Send the message again to retry.
                </div>
              )}
              {turn && (
                <>
                  <UserMessage text={turn.text} />
                  <AssistantMessage
                    blocks={turn.blocks}
                    status={panel.isRunning ? undefined : turn.status}
                    error={turn.error}
                    isRunning={panel.isRunning}
                    isAskPending={!panel.isRunning}
                    onAnswer={panel.send}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-1 border-t p-3">
        <ContextChip context={panel.context} isIncluded={panel.isContextIncluded} onToggle={panel.toggleContext} />
        <Composer isRunning={panel.isRunning} onSend={panel.send} onStop={panel.stop} />
      </div>
    </AgentRefsContext.Provider>
  );
};

export default Chat;
